import React, { useState, useEffect, useRef } from 'react';
import { userAppContext } from '../../context/Context';
import { useChatBot } from '../../hooks/useChatBot';
import { useChatStorage } from '../../hooks/useChatStorage';
import { useFeedbackConsent } from '../../hooks/useFeedbackConsent';
import './ChatWidget.css';

interface Message {
  id: string;
  sender: 'user' | 'bot';
  text: string;
  timestamp: string;
  isConsentRequest?: boolean;
}

export default function ChatWidget() {
  const context = userAppContext();
  const isAuthenticated = context?.isAuthenticated || false;
  const userEmail = context?.user?.email || null;
  const [isOpen, setIsOpen] = useState(false);
  const [inputMessage, setInputMessage] = useState('');
  const [pendingFeedbackMessage, setPendingFeedbackMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Custom hooks
  const { isLoading, sendMessage } = useChatBot(isAuthenticated, userEmail);
  const { messages, setMessages, addMessage, resetMessages, WELCOME_MESSAGE } = useChatStorage(isAuthenticated);
  const { feedbackConsent, saveFeedbackConsent } = useFeedbackConsent(isAuthenticated, userEmail);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleConsentResponse = async (consent: boolean) => {
    await saveFeedbackConsent(consent);

    const responseMessage: Message = {
      id: `bot-consent-response-${Date.now()}`,
      sender: 'bot',
      text: consent
        ? 'Thank you! Your feedback will include your email for follow-up.'
        : 'Understood. Your feedback will be sent anonymously.',
      timestamp: new Date().toISOString(),
    };
    addMessage(responseMessage);

    if (pendingFeedbackMessage) {
      await sendFeedbackToBackend(pendingFeedbackMessage);
      setPendingFeedbackMessage(null);
    }
  };

  const sendFeedbackToBackend = async (message: string) => {
    try {
      const context = formatConversationContext(messages);
      const messageWithContext = `${message}\n\n--- Previous Conversation ---\n${context}`;

      const data = await sendMessage(messageWithContext);
      if (!data) throw new Error('No response from server');

      const botMessage: Message = {
        id: `bot-${Date.now()}`,
        sender: 'bot',
        text: data.message,
        timestamp: new Date().toISOString(),
      };

      addMessage(botMessage);
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}`,
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      addMessage(errorMessage);
    }
  };

  const handleSendMessage = async () => {
    const trimmedMessage = inputMessage.trim();
    if (!trimmedMessage || isLoading) return;

    const userMessage: Message = {
      id: `user-${Date.now()}`,
      sender: 'user',
      text: trimmedMessage,
      timestamp: new Date().toISOString(),
    };

    addMessage(userMessage);
    setInputMessage('');

    try {
      const messageWithContext = createMessageWithContext(trimmedMessage, [...messages, userMessage]);

      const data = await sendMessage(messageWithContext);
      if (!data) throw new Error('No response from server');

      if (data.isFeedback && isAuthenticated && feedbackConsent === null) {
        setPendingFeedbackMessage(trimmedMessage);
        const consentMessage: Message = {
          id: `bot-consent-${Date.now()}`,
          sender: 'bot',
          text: 'To help us follow up on your feedback, would you like to include your email address? This is optional and you can change this preference anytime.',
          timestamp: new Date().toISOString(),
          isConsentRequest: true,
        };
        addMessage(consentMessage);
      } else {
        const botMessage: Message = {
          id: `bot-${Date.now()}`,
          sender: 'bot',
          text: data.message,
          timestamp: new Date().toISOString(),
        };
        addMessage(botMessage);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage: Message = {
        id: `bot-error-${Date.now()}`,
        sender: 'bot',
        text: 'Sorry, something went wrong. Please try again.',
        timestamp: new Date().toISOString(),
      };
      addMessage(errorMessage);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp: string): string => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const formatMessageWithLinks = (text: string): string => {
    return text
      .replace(/\/([\w-]+)/g, match => {
        return `<a href="${match}" class="chat-link" target="_self">${match}</a>`;
      })
      .replace(/(https?:\/\/[^\s]+)/g, match => {
        return `<a href="${match}" class="chat-link" target="_blank" rel="noopener noreferrer">${match}</a>`;
      });
  };

  const formatConversationContext = (messages: Message[]): string => {
    const relevantMessages = messages.filter(msg => !msg.isConsentRequest && msg.id !== 'welcome').slice(-5);

    return relevantMessages
      .map(msg => {
        const time = formatTime(msg.timestamp);
        const sender = msg.sender === 'user' ? 'User' : 'Assistant';
        return `[${time}] ${sender}: ${msg.text}`;
      })
      .join('\n');
  };

  const createMessageWithContext = (originalMessage: string, currentMessages: Message[]): string => {
    const context = formatConversationContext(currentMessages);

    if (context.trim() === '') {
      return originalMessage;
    }

    const messageWithContext = `${originalMessage}\n\n--- Previous Conversation ---\n${context}`;
    return messageWithContext;
  };

  return (
    <>
      <div className={`chat-widget-container ${isOpen ? 'open' : ''}`}>
        {isOpen && (
          <div className="chat-widget-window">
            <div className="chat-widget-header">
              <div className="chat-widget-header-content">
                <i className="bi bi-robot"></i>
                <span>Support Bot</span>
              </div>
              <button className="chat-widget-close" onClick={() => setIsOpen(false)} aria-label="Close chat">
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="chat-widget-disclaimer">
              <small className="text-muted">
                This assistant uses Groq-accelerated models to provide responses based on DISGENET website.
                The information generated may contain inaccuracies or hallucinations and should be
                independently verified.
              </small>
            </div>

            <div className="chat-widget-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`chat-widget-message ${msg.sender}`}>
                  <div className="chat-widget-message-content">
                    <p dangerouslySetInnerHTML={{ __html: formatMessageWithLinks(msg.text) }} />
                    <span className="chat-widget-message-time">{formatTime(msg.timestamp)}</span>
                    {msg.isConsentRequest && (
                      <div className="chat-widget-consent-buttons">
                        <button
                          className="chat-widget-consent-btn accept"
                          onClick={() => handleConsentResponse(true)}
                        >
                          Yes, include my email
                        </button>
                        <button
                          className="chat-widget-consent-btn decline"
                          onClick={() => handleConsentResponse(false)}
                        >
                          No, send anonymously
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {isLoading && (
                <div className="chat-widget-message bot">
                  <div className="chat-widget-message-content">
                    <div className="chat-widget-typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-widget-input-container">
              <textarea
                className="chat-widget-input"
                placeholder="Type your message..."
                value={inputMessage}
                onChange={e => setInputMessage(e.target.value)}
                onKeyDown={handleKeyPress}
                rows={1}
                disabled={isLoading}
              />
              <button
                className="chat-widget-send"
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isLoading}
                aria-label="Send message"
              >
                <i className="bi bi-send-fill"></i>
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        className={`chat-widget-toggle ${isOpen ? 'hidden' : ''}`}
        onClick={() => setIsOpen(true)}
        aria-label="Open chat"
      >
        <i className="bi bi-chat-dots-fill"></i>
      </button>
    </>
  );
}
